"use client";

import { useEffect, useMemo, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useSession } from "next-auth/react";
import { Check, Globe, Mail, MapPin, Search, SlidersHorizontal } from "lucide-react";
import { PillBadge } from "@/components/ui/pill-badge";
import SortByDropdown from "@/components/menu/SortByDropdown";
import WriteReviewAuthDialog from "@/components/auth/WriteReviewAuthDialog";
import WriteCompanyReviewDialog from "@/components/reviews/WriteCompanyReviewDialog";
import LoadingSpinner from "@/components/loadingComponents/loadingSpinner";
import GlassPlayButton from "@/components/ui/glass-play-button";
import { useGetAllPublicCompanyReviewsQuery } from "@/features/company-reviews/companyReviewInjection";
import type { CompanyReviewCollection, CompanyReviewPublicDoc } from "@/types/companyReview";
import { formatPublicReviewerDisplayName } from "@/lib/utils/reviewer-display-name";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { reviewCountryCode } from "@/lib/utils/country-code";
import { cn } from "@/lib/utils";
import { getDisplayImageUrl } from "@/lib/utils/image-proxy";
import { getVideoPlaybackUrl } from "@/lib/utils/media-url";
import { ga4VideoPlay } from "@/lib/analytics/ga4-ecommerce";

type Review = {
	id: string;
	author: string;
	/** Public profile image URL when the API provides it */
	avatarUrl?: string;
	date: string;
	title: string;
	rating: number;
	content: string;
	/** Reviewer country code, e.g. GB, US, FR */
	region: string;
	videoUrl?: string;
	videoThumbnailUrl?: string;
	/** Shown under author, e.g. Verified purchase / Custom project */
	badge?: string;
	/** YYYY-MM-DD for sorting (from API reviewDate / createdAt) */
	isoDate?: string;
	/** Submission time for tie-break when two reviews share the same calendar review date */
	submittedAtMs?: number;
};

function formatYyyyMmDdDisplay(ymd: string): string {
	const [y, m, d] = ymd.split("-").map(Number);
	if (!y || !m || !d) return ymd;
	return new Date(y, m - 1, d).toLocaleDateString("en-GB", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
}

function mapCompanyReviewToReview(doc: CompanyReviewPublicDoc): Review {
	const u = doc.userId;
	const author = formatPublicReviewerDisplayName(u.firstName, u.lastName, u.username);
	const isoDate =
		doc.reviewDate ??
		(doc.createdAt && doc.createdAt.length >= 10 ? doc.createdAt.slice(0, 10) : undefined);
	const date = doc.reviewDate
		? formatYyyyMmDdDisplay(doc.reviewDate)
		: new Date(doc.createdAt).toLocaleDateString("en-GB", {
				month: "short",
				day: "numeric",
				year: "numeric",
			});
	const avatar = doc.userId?.avatar?.trim();
	const submittedAtMs = doc.createdAt ? Date.parse(doc.createdAt) : undefined;
	return {
		id: `api-${doc._id}`,
		author,
		avatarUrl: avatar ? (getDisplayImageUrl(avatar) ?? avatar) : undefined,
		date,
		title: doc.title,
		rating: doc.rating,
		content: doc.body ?? "",
		region: reviewCountryCode(doc),
		videoUrl: doc.videoUrl,
		videoThumbnailUrl: doc.videoThumbnailUrl,
		badge: doc.isCustomProject ? "Custom project" : "Verified purchase",
		isoDate,
		submittedAtMs: Number.isFinite(submittedAtMs) ? submittedAtMs : undefined,
	};
}


const REVIEW_SORTS = {
	default: "Default",
	"createdAt:desc": "Newest review date",
	"createdAt:asc": "Oldest review date",
	"rating:desc": "Highest rating",
	"rating:asc": "Lowest rating",
} as const;

const REVIEWS_PER_PAGE = 20;
const PAGINATION_WINDOW = 5;

function getReviewPaginationWindow(currentPage: number, totalPages: number) {
	if (totalPages <= PAGINATION_WINDOW) {
		return {
			pageNumbers: Array.from({ length: totalPages }, (_, i) => i + 1),
			showLeadingFade: false,
			showTrailingFade: false,
		};
	}
	const half = Math.floor(PAGINATION_WINDOW / 2);
	const start = Math.min(
		Math.max(1, currentPage - half),
		totalPages - PAGINATION_WINDOW + 1,
	);
	const pageNumbers = Array.from({ length: PAGINATION_WINDOW }, (_, i) => start + i);
	return {
		pageNumbers,
		showLeadingFade: start > 1,
		showTrailingFade: start + PAGINATION_WINDOW - 1 < totalPages,
	};
}

function PaginationFadeEllipsis() {
	return (
		<span
			className="pointer-events-none flex shrink-0 select-none items-center gap-0.5 px-0.5 text-xs leading-none text-muted-foreground"
			aria-hidden
		>
			<span className="opacity-[0.45]">·</span>
			<span className="opacity-[0.28]">·</span>
			<span className="opacity-[0.14]">·</span>
		</span>
	);
}

function Stars({ rating }: { rating: number }) {
	return (
		<div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
			{Array.from({ length: 5 }).map((_, index) => (
				<span
					key={index}
					className={`inline-flex h-5 w-5 items-center justify-center rounded-none text-sm leading-none ${
						index < rating ? "bg-tertiary text-white" : "bg-muted text-muted-foreground/50"
					}`}
				>
					★
				</span>
			))}
		</div>
	);
}

type ReviewsPageClientProps = {
	/** From RSC (`getCompanyReviewsForSeo`) — skips redundant client-side full catalog fetch when present. */
	initialReviews: CompanyReviewCollection | null;
	isModal?: boolean;
};

export default function ReviewsPageClient({ initialReviews, isModal = false }: ReviewsPageClientProps) {
	const router = useRouter();
	const searchParams = useSearchParams();
	const { data: session, status: sessionStatus } = useSession();
	const sortBy = searchParams.get("sortBy") ?? "default";
	const [selectedStars, setSelectedStars] = useState<number[]>([]);
	const [searchQuery, setSearchQuery] = useState("");
	const [expandedReviewIds, setExpandedReviewIds] = useState<string[]>([]);
	const [showMoreFilters, setShowMoreFilters] = useState(false);
	const [writeReviewAuthOpen, setWriteReviewAuthOpen] = useState(false);
	const [writeCompanyReviewOpen, setWriteCompanyReviewOpen] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [playingReviewVideoIds, setPlayingReviewVideoIds] = useState<string[]>([]);
	const [loadedReviewVideoIds, setLoadedReviewVideoIds] = useState<string[]>([]);

	const isLoggedIn = sessionStatus === "authenticated" && !!session;
	const fetchCatalogClientSide = initialReviews === null;
	const { data: clientCatalog, isLoading: isClientCatalogLoading } =
		useGetAllPublicCompanyReviewsQuery(undefined, { skip: !fetchCatalogClientSide });

	const publicCompanyReviews = initialReviews ?? clientCatalog;

	const isReviewsLoading = fetchCatalogClientSide && isClientCatalogLoading;

	const allReviewsCatalog = useMemo(() => {
		return (publicCompanyReviews?.docs ?? []).map(mapCompanyReviewToReview);
	}, [publicCompanyReviews]);

	useEffect(() => {
		if (searchParams.get("writeReview") !== "1") return;
		if (sessionStatus !== "authenticated" || !session?.user?.id) return;
		setWriteCompanyReviewOpen(true);
		router.replace("/reviews", { scroll: false });
	}, [searchParams, sessionStatus, session?.user?.id, router]);

	const totalReviews = allReviewsCatalog.length;
	const totalRating = allReviewsCatalog.reduce((sum, review) => sum + review.rating, 0);
	const companyRating =
		publicCompanyReviews?.averageRating != null
			? publicCompanyReviews.averageRating
			: totalReviews > 0
				? Math.round((totalRating / totalReviews) * 10) / 10
				: 0;
	const ratingBreakdown = [5, 4, 3, 2, 1].map((stars) => {
		const count = allReviewsCatalog.filter((review) => review.rating === stars).length;
		const percent = totalReviews > 0 ? Math.round((count / totalReviews) * 100) : 0;
		return { stars, percent };
	});

	const filteredReviews = useMemo(() => {
		const reviewCalendarMs = (r: Review) => {
			if (r.isoDate && /^\d{4}-\d{2}-\d{2}$/.test(r.isoDate)) {
				return new Date(`${r.isoDate}T12:00:00`).getTime();
			}
			return r.submittedAtMs ?? 0;
		};

		let result = allReviewsCatalog.filter((review) => {
			if (selectedStars.length > 0 && !selectedStars.includes(review.rating)) return false;
			return true;
		});

		const q = searchQuery.trim().toLowerCase();
		if (q) {
			const tokens = q.split(/\s+/).filter(Boolean);
			result = result.filter((review) => {
				const haystack =
					`${review.title} ${review.author} ${review.content} ${review.badge ?? ""}`.toLowerCase();
				return tokens.every((token) => haystack.includes(token));
			});
		}

		result = [...result].sort((a, b) => {
			const byCalendarThenSubmitted = (x: Review, y: Review, asc: boolean) => {
				const cx = reviewCalendarMs(x);
				const cy = reviewCalendarMs(y);
				if (cx !== cy) return asc ? cx - cy : cy - cx;
				const sx = x.submittedAtMs ?? 0;
				const sy = y.submittedAtMs ?? 0;
				return asc ? sx - sy : sy - sx;
			};
			if (sortBy === "default" || sortBy === "createdAt:desc") {
				return byCalendarThenSubmitted(a, b, false);
			}
			if (sortBy === "createdAt:asc") return byCalendarThenSubmitted(a, b, true);
			if (sortBy === "rating:desc") return b.rating - a.rating;
			if (sortBy === "rating:asc") return a.rating - b.rating;
			return byCalendarThenSubmitted(a, b, false);
		});

		return result;
	}, [selectedStars, sortBy, searchQuery, allReviewsCatalog]);

	const totalPages = Math.max(1, Math.ceil(filteredReviews.length / REVIEWS_PER_PAGE));
	const paginationWindow = useMemo(
		() => getReviewPaginationWindow(currentPage, totalPages),
		[currentPage, totalPages],
	);
	const paginatedReviews = useMemo(() => {
		const startIndex = (currentPage - 1) * REVIEWS_PER_PAGE;
		return filteredReviews.slice(startIndex, startIndex + REVIEWS_PER_PAGE);
	}, [currentPage, filteredReviews]);

	useEffect(() => {
		setCurrentPage(1);
	}, [searchQuery, selectedStars, sortBy]);

	useEffect(() => {
		if (currentPage > totalPages) {
			setCurrentPage(totalPages);
		}
	}, [currentPage, totalPages]);

	const toggleStars = (stars: number) => {
		setSelectedStars((prev) =>
			prev.includes(stars) ? prev.filter((value) => value !== stars) : [...prev, stars],
		);
	};

	const toggleExpandedReview = (reviewId: string) => {
		setExpandedReviewIds((prev) =>
			prev.includes(reviewId) ? prev.filter((id) => id !== reviewId) : [...prev, reviewId],
		);
	};

	const setReviewVideoPlaying = (reviewId: string, isPlaying: boolean) => {
		setPlayingReviewVideoIds((prev) => {
			const exists = prev.includes(reviewId);
			if (isPlaying) return exists ? prev : [...prev, reviewId];
			return exists ? prev.filter((id) => id !== reviewId) : prev;
		});
	};

	const playReviewVideo = (reviewId: string) => {
		ga4VideoPlay({ video_provider: "review", content_type: "company_review", item_id: reviewId });
		setLoadedReviewVideoIds((prev) => (prev.includes(reviewId) ? prev : [...prev, reviewId]));
		window.setTimeout(() => {
			const video = document.getElementById(`review-video-${reviewId}`) as HTMLVideoElement | null;
			if (!video) return;
			video.preload = "auto";
			video.load();
			void video.play();
		}, 0);
	};

	return (
		<div
			className={cn(
				"mx-auto w-full bg-background text-foreground",
				isModal ? "max-w-full overflow-x-hidden px-4 py-6 sm:px-6" : "max-w-6xl px-6 py-10",
			)}
		>
			<WriteReviewAuthDialog open={writeReviewAuthOpen} onOpenChange={setWriteReviewAuthOpen} />
			{session?.user?.id ? (
				<WriteCompanyReviewDialog
					open={writeCompanyReviewOpen}
					onOpenChange={setWriteCompanyReviewOpen}
					userId={session.user.id}
				/>
			) : null}
			<section className="py-2">
				<div
					className={cn(
						"flex flex-col gap-8",
						!isModal && "lg:flex-row lg:items-stretch lg:justify-between",
					)}
				>
					<div className="flex-1">
						<div className="flex items-start gap-4">
							<div className="flex items-start gap-4">
								<div className="relative h-[6.5rem] w-[6.5rem] overflow-hidden rounded-md border border-secondary/20 bg-background-secondary">
									<img src="/Main Icon.jpg" alt="Techxeed" className="size-full object-cover" />
								</div>
								<div>
									<h1 className="text-2xl font-semibold text-foreground">Techxeed Reviews</h1>
									<div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
										<span className="text-muted-foreground">{totalReviews} reviews</span>
										<Stars
											rating={Math.min(5, Math.max(1, Math.round(companyRating)))}
										/>
										<span className="font-medium text-foreground">{companyRating.toFixed(1)}</span>
									</div>
									<p className="mt-1 text-sm text-secondary">AI &amp; Software company</p>
								</div>
							</div>
						</div>

						<div className="mt-6 border-t border-secondary/20 pt-6">
							<div className="mb-3 flex flex-wrap items-center gap-2">
								<h2 className="text-lg font-semibold">Company details</h2>
								<PillBadge size="sm" className="text-xs font-normal text-muted-foreground shadow-none">
									AI &amp; Software company
								</PillBadge>
							</div>
							<p className="max-w-3xl text-sm leading-6 text-muted-foreground">
								Techxeed builds software and AI systems that help coaches, consultants and agencies
								scale with less manual admin. We design automation-first digital infrastructure
								that helps teams run faster and more consistently.
							</p>
						</div>

						<div className="mt-8">
							<h3 className="text-lg font-semibold">Contact info</h3>
							<div className="mt-3 space-y-2 text-sm text-muted-foreground">
								<p className="flex items-center gap-2">
									<MapPin className="size-4 text-secondary" />
									124 City Road, EC1V 2NX, London, United Kingdom
								</p>
								<p className="flex items-center gap-2">
									<Mail className="size-4 text-secondary" />
									support@techxeed.com
								</p>
								<p className="flex items-center gap-2">
									<Globe className="size-4 text-secondary" />
									techxeed.com
								</p>
							</div>
						</div>
					</div>

					<div
						className={cn(
							"flex w-full flex-col gap-5",
							isModal ? "max-w-full" : "max-w-sm lg:gap-0 lg:justify-end",
						)}
					>
						<div className="rounded-2xl border border-secondary/20 bg-card p-6 shadow-custom">
							<div className="flex items-start gap-5">
								<div className="min-w-[92px]">
									<p className="text-[50px] font-semibold leading-none text-foreground">
										{companyRating.toFixed(1)}
									</p>
									<p className="mt-1 text-sm font-medium leading-none text-foreground">Excellent!</p>
									<div className="mt-2">
										<Stars
											rating={Math.min(5, Math.max(1, Math.round(companyRating)))}
										/>
									</div>
									<p className="mt-2 text-xs text-muted-foreground">{totalReviews} reviews</p>
								</div>

								<div className="flex-1 space-y-2 pt-1">
									{ratingBreakdown.map((row) => (
										<div key={row.stars} className="grid grid-cols-[52px_1fr] items-center gap-2 text-xs">
											<span className="text-muted-foreground">{row.stars}-star</span>
											<div className="h-2 rounded-full bg-muted">
												<div
													className="h-2 rounded-full bg-tertiary"
													style={{ width: `${row.percent}%` }}
												/>
											</div>
										</div>
									))}
								</div>
							</div>
						</div>
						{isLoggedIn ? (
							<button
								type="button"
								onClick={() => setWriteCompanyReviewOpen(true)}
								className="mt-0 inline-flex h-10 items-center justify-center self-end rounded-full bg-secondary px-5 text-sm font-medium text-white transition hover:bg-secondary/90 lg:mt-auto"
							>
								Write a review
							</button>
						) : (
							<button
								type="button"
								onClick={() => setWriteReviewAuthOpen(true)}
								className="mt-0 inline-flex h-10 items-center justify-center self-end rounded-full bg-secondary px-5 text-sm font-medium text-white transition hover:bg-secondary/90 lg:mt-auto"
							>
								Write a review
							</button>
						)}
					</div>
				</div>
			</section>

			<section className={cn("mt-10 grid gap-8", !isModal && "lg:grid-cols-[280px_1fr]")}>
				<aside className="p-1">
					<div className="flex items-center gap-2">
						<span className="text-xl text-tertiary">★</span>
						<span className="text-4xl font-semibold leading-none">{companyRating.toFixed(1)}</span>
					</div>

					<h2 className="mt-6 text-2xl font-semibold leading-none">All reviews</h2>
					<div className="mt-4 flex items-center gap-2 text-sm">
						<span className="text-muted-foreground">{totalReviews} total</span>
						<span className="text-muted-foreground">•</span>
						{isLoggedIn ? (
							<button
								type="button"
								onClick={() => setWriteCompanyReviewOpen(true)}
								className="font-medium text-secondary underline-offset-4 hover:underline"
							>
								Write a review
							</button>
						) : (
							<button
								type="button"
								onClick={() => setWriteReviewAuthOpen(true)}
								className="font-medium text-secondary underline-offset-4 hover:underline"
							>
								Write a review
							</button>
						)}
					</div>
					<div className="mt-6 rounded-2xl border border-secondary/20 bg-card p-4">
						{ratingBreakdown.map((row) => {
							const isChecked = selectedStars.includes(row.stars);
							return (
								<button
									key={`left-${row.stars}`}
									type="button"
									onClick={() => toggleStars(row.stars)}
									className="mb-2 flex w-full items-center gap-2 text-xs last:mb-0"
									aria-pressed={isChecked}
									aria-label={`Filter by ${row.stars} star reviews`}
								>
									<span className="inline-flex h-5 w-5 items-center justify-center border border-secondary/20 bg-card">
										{isChecked ? <Check className="size-3 text-tertiary" /> : null}
									</span>
									<span className="text-muted-foreground">{row.stars}-star</span>
									<div className="h-2 flex-1 rounded-full bg-muted">
										<div
											className="h-2 rounded-full bg-tertiary"
											style={{ width: `${row.percent}%` }}
										/>
									</div>
									<span className="text-muted-foreground">{row.percent}%</span>
								</button>
							);
						})}
					</div>
				</aside>

				<div>
					<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
						<div className="relative flex-1 sm:min-w-[220px]">
							<Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
							<input
								type="search"
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								placeholder="Search by keyword..."
								aria-label="Search reviews by keyword"
								className="h-11 w-full rounded-full border border-secondary/20 bg-card pl-10 pr-4 text-sm outline-none transition focus:border-secondary"
							/>
						</div>
						<button
							type="button"
							onClick={() => setShowMoreFilters((prev) => !prev)}
							className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-full border border-secondary/20 px-4 text-sm text-muted-foreground"
						>
							<SlidersHorizontal className="size-4" />
							{selectedStars.length > 0 ? `More filters (${selectedStars.length})` : "More filters"}
						</button>
						<div className="w-full shrink-0 sm:w-auto sm:min-w-0">
							<SortByDropdown
								defaultSort="default"
								removeParamWhen="default"
								sorts={REVIEW_SORTS}
								glassToolbar
							/>
						</div>
					</div>

					{showMoreFilters ? (
						<div className="mt-3 rounded-xl border border-secondary/20 bg-card p-4">
							<div className="flex flex-wrap items-center gap-3">
								{[5, 4, 3, 2, 1].map((stars) => {
									const isChecked = selectedStars.includes(stars);
									return (
										<button
											key={`more-filter-stars-${stars}`}
											type="button"
											onClick={() => toggleStars(stars)}
											className="inline-flex items-center gap-2 rounded-full border border-secondary/20 px-3 py-1.5 text-xs text-muted-foreground"
											aria-pressed={isChecked}
											aria-label={`Filter by ${stars} star reviews`}
										>
											<span className="inline-flex h-4 w-4 items-center justify-center border border-secondary/20 bg-card">
												{isChecked ? <Check className="size-3 text-tertiary" /> : null}
											</span>
											<span>{stars}-star</span>
										</button>
									);
								})}
							</div>
						</div>
					) : null}

					<div className="mt-6 space-y-4">
						{isReviewsLoading ? (
							<div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-secondary/20 bg-card py-16">
								<LoadingSpinner fullScreen={false} />
								<p className="text-sm text-muted-foreground">Loading reviews…</p>
							</div>
						) : null}
						{!isReviewsLoading && filteredReviews.length === 0 ? (
							<p className="rounded-xl border border-secondary/20 bg-card p-8 text-center text-sm text-muted-foreground">
								{allReviewsCatalog.length === 0
									? "No published reviews yet. Approved submissions will appear here."
									: "No reviews match your search or filters. Try different keywords or clear the star filters."}
							</p>
						) : null}
						{paginatedReviews.map((review) => {
							const isExpanded = expandedReviewIds.includes(review.id);
							const normalizedContent = review.content.replace(/\s+/g, " ").trim();
							const words = normalizedContent.length > 0 ? normalizedContent.split(/\s+/) : [];
							const isLongReview = words.length > 100;
							const previewContent = isLongReview
								? `${words.slice(0, 100).join(" ")}...`
								: normalizedContent;

							return (
								<article
									key={review.id}
									className="rounded-xl border border-secondary/20 bg-card p-5 shadow-custom"
								>
									<div className="flex items-start justify-between gap-4">
										<div className="flex items-center gap-3">
											<Avatar className="size-9 border border-secondary/10">
												<AvatarImage src={review.avatarUrl ?? ""} alt="" />
												<AvatarFallback className="bg-secondary/15 text-sm font-semibold text-secondary">
													{review.author.charAt(0)}
												</AvatarFallback>
											</Avatar>
											<div>
												<p className="text-sm font-medium">{review.author}</p>
												<p className="text-xs text-muted-foreground">
													{review.region ?? "GB"} • {review.badge ?? "Verified purchase"}
												</p>
											</div>
										</div>
										<p className="text-xs text-muted-foreground">{review.date}</p>
									</div>

									<div className="mt-3">
										<Stars rating={review.rating} />
										<div>
											<h3 className="mt-3 text-base font-semibold">{review.title}</h3>
											{normalizedContent ? (
												<p
													className={`mt-2 text-sm leading-6 text-muted-foreground ${
														isExpanded ? "whitespace-pre-line" : "whitespace-normal"
													}`}
												>
													{isExpanded ? review.content : previewContent}
												</p>
											) : null}
											{review.videoUrl ? (
												<div className="mt-4 w-full max-w-36 sm:max-w-44">
													{/* 9:16 frame (1080×1920 portrait), compact so it doesn’t dominate the card */}
													<div
														className="no-media-save group relative aspect-[9/16] w-full overflow-hidden rounded-lg"
														onContextMenu={(event) => event.preventDefault()}
													>
														<video
															id={`review-video-${review.id}`}
															src={
																loadedReviewVideoIds.includes(review.id)
																	? getVideoPlaybackUrl(review.videoUrl)
																	: undefined
															}
															controls={playingReviewVideoIds.includes(review.id)}
															controlsList="nodownload noplaybackrate noremoteplayback"
															disablePictureInPicture
															disableRemotePlayback
															className="size-full object-contain"
															style={
																review.videoThumbnailUrl &&
																!loadedReviewVideoIds.includes(review.id)
																	? { opacity: 0 }
																	: undefined
															}
															preload={loadedReviewVideoIds.includes(review.id) ? "auto" : "none"}
															onContextMenu={(event) => event.preventDefault()}
															onPlay={() => setReviewVideoPlaying(review.id, true)}
															onPause={() => setReviewVideoPlaying(review.id, false)}
															onEnded={() => setReviewVideoPlaying(review.id, false)}
														>
															Your browser does not support the video tag.
														</video>
														{review.videoThumbnailUrl && !loadedReviewVideoIds.includes(review.id) ? (
															<img
																src={getDisplayImageUrl(review.videoThumbnailUrl) ?? review.videoThumbnailUrl}
																alt=""
																aria-hidden
																className="absolute inset-0 size-full scale-[1.035] object-cover"
																draggable={false}
															/>
														) : null}
														{!playingReviewVideoIds.includes(review.id) ? (
															<div className="pointer-events-none absolute inset-0 flex items-center justify-center">
																<GlassPlayButton
																	onClick={() => playReviewVideo(review.id)}
																	size="lg"
																	aria-label={`Play video review by ${review.author}`}
																/>
															</div>
														) : null}
													</div>
												</div>
											) : null}
											{isLongReview && normalizedContent ? (
												<button
													type="button"
													onClick={() => toggleExpandedReview(review.id)}
													className="mt-1 text-sm font-medium text-secondary hover:underline"
												>
													{isExpanded ? "See less" : "See more"}
												</button>
											) : null}
										</div>
									</div>
								</article>
							);
						})}
					</div>
					{filteredReviews.length > REVIEWS_PER_PAGE ? (
						<div className="mt-6 flex max-w-full flex-nowrap items-center justify-center gap-1.5 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] sm:gap-2 [&::-webkit-scrollbar]:hidden">
							<button
								type="button"
								onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
								disabled={currentPage === 1}
								className="inline-flex h-8 shrink-0 items-center rounded-full border border-secondary/20 px-3 text-xs text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
							>
								Previous
							</button>
							{paginationWindow.showLeadingFade ? <PaginationFadeEllipsis /> : null}
							{paginationWindow.pageNumbers.map((pageNumber) => {
								const isActive = pageNumber === currentPage;
								return (
									<button
										key={`reviews-page-${pageNumber}`}
										type="button"
										onClick={() => setCurrentPage(pageNumber)}
										aria-current={isActive ? "page" : undefined}
										className={`inline-flex h-8 min-w-8 shrink-0 items-center justify-center rounded-full border px-2 text-xs transition ${
											isActive
												? "border-secondary bg-secondary text-white"
												: "border-secondary/20 text-muted-foreground hover:border-secondary/40"
										}`}
									>
										{pageNumber}
									</button>
								);
							})}
							{paginationWindow.showTrailingFade ? <PaginationFadeEllipsis /> : null}
							<button
								type="button"
								onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
								disabled={currentPage === totalPages}
								className="inline-flex h-8 shrink-0 items-center rounded-full border border-secondary/20 px-3 text-xs text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
							>
								Next
							</button>
						</div>
					) : null}
				</div>
			</section>
		</div>
	);
}
